import InputField from "components/common/Input/Input";
import SelectOption from "components/common/Select/Select";
import { CORE_CATEGORIES, getParticipantLabel } from "Constants/constant";
import { apiPostRequest } from "helpers/Requests";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { URL_ROSTER_LINK_TO_CORE_FUNDING } from "store/services/URL";
import { formatAppliedRateExact } from "pages/Rostering/UpdatedTimeSheet/utils/codeAllocationRates";
import { formatHours } from "Utils/utils";
import { applyWorkerParticipantRatio } from "../../../UpdatedTimeSheet/TimeSheetDetailsSection/TabSection/NewCodeAllocation/v5BillingUtils";

const ActiveHours = ({
  watchFormData,
  index,
  register,
  itemNumber = [],
  setValue,
  clearErrors,
  item,
  selectedTimeSheet,
  disabledAllComponent = false,
  isNonBillable,
  totalBreakdownLength,
  alreadySelectedItemNumber,
    isEditMode = false,
  serviceCategory,
  spRosteringSetting,
  spSettings,
  useExactAppliedRate = false,
}) => {
  const participantLabelPlural = getParticipantLabel(spRosteringSetting?.care_sector, true);
  const [selectedItemNumber, setSelectedItemNumber] = useState("");
    const [activeHoursError, setActiveHoursError] = useState(false);

    const [isCheckingBudget, setIsCheckingBudget] = useState(false);
    const [budgetCheckResults, setBudgetCheckResults] = useState([]);
    const [successfulBudgetChecks, setSuccessfulBudgetChecks] = useState([]);

  const val = watchFormData[`invoice_item_id_active_hours${index}`];
  const hydratedActiveHoursKeyRef = useRef(null);

  const itemNumberKey = useMemo(
    () => (itemNumber || []).map((x) => x.value).join(","),
    [itemNumber]
  );

  const activeHoursLineItemOption = useMemo(() => {
    const itemId = watchFormData[`invoice_item_id_active_hours${index}`];
    if (!itemId || !itemNumber?.length) {
      return "";
    }
    return itemNumber.find((x) => x.value === itemId) ?? "";
  }, [watchFormData[`invoice_item_id_active_hours${index}`], itemNumberKey, itemNumber]);

  useEffect(() => {
    if (item && val) {
      const selectedItem = itemNumber?.find((opt) => opt.value === val);
      if (selectedItem) {
        setSelectedItemNumber((prev) =>
          prev?.value === selectedItem.value ? prev : selectedItem
        );
      }
    }
  }, [itemNumber, val, item]);

  useEffect(() => {
    if (selectedItemNumber?.number) {
      setValue(
        `invoice_item_name_active_hours${index}`,
        selectedItemNumber.number
      );
    }
  }, [selectedItemNumber?.number, index, setValue]);

  const NDIS_RATE = useMemo(
    () => [
      {
        label: "Apply Alternative Rate",
        value: "alternate",
      },
      {
        label:
          selectedItemNumber?.price != null
            ? String(selectedItemNumber.price)
            : "",
        value:
          selectedItemNumber?.price != null ? selectedItemNumber.price : "",
      },
    ],
    [selectedItemNumber?.price]
  );

  const activeHoursChildUsesAlternateRate = useCallback(
    (child, cataloguePrice) => {
      if (!child || cataloguePrice == null || cataloguePrice === "") {
        return false;
      }
      const saved = Number(child?.invoice_price);
      const catalogue = Number(cataloguePrice);
      return (
        !isNaN(saved) &&
        !isNaN(catalogue) &&
        saved !== catalogue
      );
    },
    []
  );

  useEffect(() => {
    if (!isEditMode || !item?.children?.length) return;

    const child = item.children[0];
    const hydrationKey =
      child?.id != null
        ? `id:${child.id}`
        : child?.uuid
          ? `uuid:${child.uuid}`
          : null;
    if (!hydrationKey) return;

    const cataloguePrice =
      itemNumber?.find((x) => x.value === child?.invoice_item_id)?.price ??
      null;

    if (child?.invoice_item_id && cataloguePrice == null) {
      return;
    }

    const rateHydrationKey = `${hydrationKey}:${cataloguePrice ?? "none"}`;
    if (hydratedActiveHoursKeyRef.current === rateHydrationKey) {
      return;
    }
    hydratedActiveHoursKeyRef.current = rateHydrationKey;

    setValue(`active_hours_${index}`, formatHours(child?.duration));
    setValue(`invoice_item_id_active_hours${index}`, child?.invoice_item_id);

    if (activeHoursChildUsesAlternateRate(child, cataloguePrice)) {
      setValue(`invoice_ndis_rate_active_hours${index}`, "alternate");
      if (child?.invoice_price != null && child?.invoice_price !== "") {
        setValue(`rate_amount_active_hours${index}`, child.invoice_price);
      }
    } else if (child?.invoice_price != null && child?.invoice_price !== "") {
      setValue(`invoice_ndis_rate_active_hours${index}`, child.invoice_price);
      setValue(`rate_amount_active_hours${index}`, child.invoice_price);
    } else if (cataloguePrice != null && cataloguePrice !== "") {
      setValue(`invoice_ndis_rate_active_hours${index}`, cataloguePrice);
      setValue(`rate_amount_active_hours${index}`, cataloguePrice);
    }
  }, [
    isEditMode,
    item?.children,
    item?.id,
    itemNumber,
    index,
    setValue,
    activeHoursChildUsesAlternateRate,
  ]);
  const getAppliedAmount = () => {
    const hourlyRate = watchFormData?.[`invoice_ndis_rate_active_hours${index}`];
    if (!watchFormData || !hourlyRate) return "";

    const rateAmount = watchFormData[`rate_amount_active_hours${index}`];
    const baseRate =
      hourlyRate !== "alternate" ? Number(hourlyRate) : Number(rateAmount);
    if (Number.isNaN(baseRate)) return "";

    const ratio =
      watchFormData?.[`ratio_${index}`] ||
      item?.ratio ||
      selectedTimeSheet?.ratio ||
      null;
    const ratioApplied = applyWorkerParticipantRatio(baseRate, ratio);
    const participantCount = selectedTimeSheet?.participants?.length || 1;
    const result =
      ratioApplied != null ? ratioApplied : baseRate / participantCount;
    if (Number.isNaN(result)) return "";

    return useExactAppliedRate
      ? formatAppliedRateExact(result)
      : result.toFixed(2);
  };

  const resolvedActiveHoursNdisRate =
    watchFormData[`invoice_ndis_rate_active_hours${index}`];

  const hourlyRateSelectedOption = useMemo(() => {
    if (
      resolvedActiveHoursNdisRate == null ||
      resolvedActiveHoursNdisRate === ""
    ) {
      return null;
    }
    return (
      NDIS_RATE.find(
        (x) => String(x.value) === String(resolvedActiveHoursNdisRate)
      ) ?? null
    );
  }, [NDIS_RATE, resolvedActiveHoursNdisRate]);

  useEffect(() => {
    const ndisRate = watchFormData[`invoice_ndis_rate_active_hours${index}`];
    // A $0 catalogue rate is a real selection — only skip genuinely empty values.
    if (
      selectedItemNumber?.price != null &&
      selectedItemNumber?.price !== "" &&
      ndisRate != null &&
      ndisRate !== "" &&
      ndisRate !== "alternate"
    ) {
      const currentAmount = watchFormData[`rate_amount_active_hours${index}`];
      if (String(currentAmount) !== String(ndisRate)) {
        setValue(`rate_amount_active_hours${index}`, ndisRate);
      }
    }
  }, [
    watchFormData[`invoice_ndis_rate_active_hours${index}`],
    selectedItemNumber?.price,
    index,
    setValue,
  ]);

  let activeHours = watchFormData[`active_hours_${index}`];
  useEffect(() => {
    const timeToMinutes = (time) => {
      const [hours, minutes] = time.split(":").map(Number);
      return hours * 60 + minutes;
    };

    if (activeHours && totalBreakdownLength) {
      const activeHoursInMinutes = timeToMinutes(activeHours);
      const totalBreakdownInMinutes = timeToMinutes(totalBreakdownLength);

      if (activeHoursInMinutes > totalBreakdownInMinutes) {
        setActiveHoursError(true);
        setTimeValidationError(false);
      } else {
        setActiveHoursError(false);
      }
    }
  }, [activeHours, totalBreakdownLength]);

  useEffect(() => {
    if (activeHours) {
      if (!validateTimeFormat(activeHours)) {
        setTimeValidationError(true);
      } else {
        setTimeValidationError(false);
      }
    }
  }, [activeHours]);
  function formatTime(totalBreakdownLength) {
    if (!totalBreakdownLength) return;
    const timeRegex = /^(\d{1,2}):([0-5]?\d)$/;
    const match = totalBreakdownLength.match(timeRegex);

    if (!match) {
      return "Invalid time format. Please use HH:MM.";
    }

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);

    return `${hours} hours and ${minutes} minutes`;
  }
  const [timeValidationError, setTimeValidationError] = useState(false);
  const validateTimeFormat = (time) => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    return timeRegex.test(time);
    };

const checkPlanBudget = async (ndisItemId, participantId, participantName) => {
 const customLineItemUuid =
   typeof ndisItemId === "string" && ndisItemId.startsWith("custom:")
     ? ndisItemId.replace("custom:", "")
     : null;

 const isLinkRosteringEnabled = spRosteringSetting?.link_rostering_core_funding || spSettings?.link_rostering_core_funding;
    if (!isLinkRosteringEnabled || !ndisItemId || !participantId) {
        return { success: true };
    }
    const payload = {
        price_catalog_id: ndisItemId,
        custom_line_item_uuid: customLineItemUuid,
        pt_id: participantId,
        timesheet_id: selectedTimeSheet?.id ?? null,
        timesheet_uuid: selectedTimeSheet?.uuid ?? null,
        invoice_timesheet_id: item?.id ?? null,
        invoice_timesheet_uuid: item?.uuid ?? null,
        prefer_snapshot:
            String(selectedTimeSheet?.status || "").toLowerCase() === "approved" ||
            String(selectedTimeSheet?.progress || "").toLowerCase() === "pay_approved",
    };
    try {
        const response = await apiPostRequest(URL_ROSTER_LINK_TO_CORE_FUNDING, payload);
        if (response?.status === 200) {
            const resultMap = response?.data?.data?.items?.results || {};
            const raw =
                resultMap?.[participantId] ?? resultMap?.[String(participantId)];
            if (!raw) {
                const errors = response?.data?.data?.items?.errors;
                return {
                    success: false,
                    participantName,
                    participantId,
                    error:
                        (Array.isArray(errors) && errors.length ? errors.join(" ") : null) ||
                        "No linked funding found for this participant and line item.",
                };
            }
            return {
                success: true,
                participantName,
                data: raw?.ssb ?? raw,
                funding: {
                    contract_id: raw?.contract_id ?? null,
                    start_date: raw?.start_date ?? null,
                    end_date: raw?.end_date ?? null,
                },
            };
        }

    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || "Budget check failed";
        return {
            success: false,
            participantName,
            participantId,
            error: errorMessage
        };
    }
};

  return (
    <>
     
        <div className='border bg-white border-gray-100 text-gray-900 shadow-md shadow-indigo-100  py-7 px-6 rounded-lg mt-4'>
          <div className='flex items-center gap-x-4 border-gray-200 border-b py-5 '>
            <div className='flex flex-col w-full gap-2'>
              <label className='text-gray-900 text-base font-semibold'>
                Active Hours
              </label>
              <div className='w-full'>
                <InputField
                  cssClass='mt-0 h-40-important'
                  inputWidth='w-full'
                  isDisabled={isNonBillable}
                  type='text'
                  placeHolder='HH:MM '
                  name={`active_hours_${index}`}
                  register={register}
                />
                {activeHoursError && (
                  <p
                    className='text-red-600 text-sm'
                    style={{ textTransform: "none" }}
                  >
                    Active hours cannot exceed the total breakdown time of{" "}
                    {formatTime(totalBreakdownLength)}.
                  </p>
                )}
                {timeValidationError && (
                  <p
                    className='text-red-600 text-sm'
                    style={{ textTransform: "none" }}
                  >
                    Please enter a valid time in HH:MM format.
                  </p>
                )}
              </div>
            </div>
          </div>
          {watchFormData[`active_hours_${index}`] && (
            <>
              <div className='flex  items-center gap-x-4 border-gray-200 border-b py-5 '>
                <div className='flex flex-col w-full gap-2'>
                                  <label className='text-gray-900 text-base font-semibold'>
                    NDIS Item Name<span className='text-red-600'>*</span>
                  </label>
                  <div className='w-full'>
                    <SelectOption
                      isDisabled={!alreadySelectedItemNumber}
                      name={`invoice_item_id_active_hours${index}`}
                      data={itemNumber?.filter(
                        (item) => item?.value !== alreadySelectedItemNumber
                      )}
                      cssClass='w-full'
                      isRequired={true}
                      isSearch={true}
                      register={register}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      selectedOption={activeHoursLineItemOption}
                                         handleChange={async (selectedItem) => {
    setSelectedItemNumber(selectedItem);
    setValue(`invoice_item_id_active_hours${index}`, selectedItem?.value);
    // Picking an item re-suggests its catalogue rate; the user can still switch
    // to an alternate rate afterwards.
    if (selectedItem?.price != null) {
      setValue(`invoice_ndis_rate_active_hours${index}`, selectedItem.price);
      setValue(`rate_amount_active_hours${index}`, selectedItem.price);
    }
    setBudgetCheckResults([]);
    setSuccessfulBudgetChecks([]);
    if (!serviceCategory?.name) {
        return;
    }
    if (!selectedTimeSheet?.participants || selectedTimeSheet?.participants?.length === 0) {
        return;
    }
    setIsCheckingBudget(true);
    const checkResults = [];
    const successfulResults = [];
    try {
        if (selectedTimeSheet?.participants?.length > 1) {
            const checkPromises = selectedTimeSheet.participants.map((participant, idx) => {
                if (participant?.id) {
                    const participantName = `${participant?.first_name || ''} ${participant?.last_name || ''}`.trim();
                    return checkPlanBudget(selectedItem?.value, participant?.id, participantName);
                }
                return Promise.resolve({ success: true });
            });
            const results = await Promise.all(checkPromises);
            checkResults.push(...results.filter(r => r !== null));
        } else {
            const participant = selectedTimeSheet.participants[0];
            if (participant?.id) {
                const participantName = `${participant?.first_name || ''} ${participant?.last_name || ''}`.trim();
                const result = await checkPlanBudget(selectedItem?.value, participant?.id, participantName);
                checkResults.push(result);
            }
        }

        const failedChecks = checkResults.filter(r => !r?.success);
        const passedChecks = checkResults.filter(r => r?.success && r?.data);
        setBudgetCheckResults(failedChecks);
        setSuccessfulBudgetChecks(passedChecks);

        if (failedChecks.length > 0) {
            if (failedChecks.length === 1) {
                toast.error(failedChecks[0].error);
            } else {
                const affectedParticipants = failedChecks.map(f => f.participantName).join(', ');
                toast.error(`Budget check failed for ${participantLabelPlural.toLowerCase()}: ${affectedParticipants}`);
            }
        }
    } catch (error) {
        toast.error("Failed to check budget availability");
    } finally {
        setIsCheckingBudget(false);
    }
}}
loading={isCheckingBudget}
                                      />
                                      {isCheckingBudget && (
  <p className="text-sm text-blue-600 mt-1">
    Checking budget availability for active hours...
  </p>
)}

{budgetCheckResults.length > 0 && (
  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
    <p className="text-sm font-semibold text-red-800 mb-1">
      Budget Check Issues for Active Hours:
    </p>
    <ul className="text-sm text-red-700 space-y-1">
      {budgetCheckResults.map((result, idx) => (
        <li key={idx} className="flex items-start">
          <span className="mr-1">•</span>
          <span>
            <strong>{result.participantName}:</strong> {result.error}
          </span>
        </li>
      ))}
    </ul>
  </div>
)}

{successfulBudgetChecks.length > 0 && (
  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
    <div className="text-sm text-green-700 space-y-2">
      {successfulBudgetChecks.map((result, idx) => (
        <div key={idx}>
          <p className="font-medium">{result.participantName}</p>
          <div className="text-xs space-y-1 mt-1">
            <p>• Remaining Budget: ${parseFloat(result.data?.remain_amount || '0').toFixed(2)}</p>
            <p>• Remaining Time: {result.data?.remain_total_hours || 0}h {result.data?.remain_total_mint || 0}m</p>
          </div>
        </div>
      ))}
    </div>
  </div>
)}
                  </div>
                </div>
              </div>
              <div className='flex items-center gap-x-4 border-gray-200 border-b py-5 '>
                <div className='flex flex-col w-full gap-4'>
                  <label className='text-gray-900 text-base font-semibold'>
                    NDIS Item Number<span className='text-red-600'>*</span>
                  </label>
                  <div className='w-full'>
                    <InputField
                      //   isDisabled={disabledAllComponent}
                      readOnly={true}
                      cssClass='mt-0 h-40-important'
                      inputWidth='w-full'
                      name={`invoice_item_name_active_hours${index}`}
                      // value={selectedItemNumber.number}
                      isRequired={true}
                      register={register}
                    />
                  </div>
                  <label className='text-gray-900 text-base font-semibold w-[130px] min-w-[130px] max-w-[130px]'>
                    Hourly Rate<span className='text-red-600'>*</span>
                  </label>
                  <div className='w-full'>
                    <SelectOption
                      isDisabled={
                        isNonBillable ||
                        disabledAllComponent ||
                        !alreadySelectedItemNumber
                      }
                      data={NDIS_RATE}
                      name={`invoice_ndis_rate_active_hours${index}`}
                      cssClass='w-full'
                      isRequired={true}
                      register={register}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      selectedOption={hourlyRateSelectedOption}
                    />
                  </div>
                </div>
              </div>
              <div className='flex items-center gap-x-4 border-gray-200 border-b py-5 '>
                <div className='flex flex-col w-full  gap-4'>
                  <label className='text-gray-900 text-base font-semibold'>
                    Rate Amount <span className='text-red-600'>*</span>
                  </label>
                  <div className='w-full'>
                    {watchFormData?.[
                      `invoice_ndis_rate_active_hours${index}`
                    ] === "alternate" ? (
                      <InputField
                        isDisabled={
                          watchFormData?.non_billable || disabledAllComponent
                        }
                        cssClass='mt-0 h-40-important'
                        inputWidth='w-full'
                        setValue={setValue}
                        // value={watchFormData?.rate_amount_active_hours[`${index}`]}
                        name={`rate_amount_active_hours${index}`}
                        isRequired={true}
                        register={register}
                        type='number'
                        step='0.01'
                      />
                    ) : (
                      <InputField
                        isDisabled={true || disabledAllComponent}
                        cssClass='mt-0 h-40-important'
                        inputWidth='w-full'
                        name={`rate_amount_active_hours${index}`}
                        isRequired={true}
                        register={register}
                        setValue={setValue}
                      />
                    )}
                  </div>
                  <label className='text-gray-900 text-base font-semibold w-[130px] min-w-[130px] max-w-[130px]'>
                    Applied Rate
                  </label>
                  <div className='w-full'>
                    <InputField
                      isDisabled={true || disabledAllComponent}
                      cssClass='mt-0 h-40-important'
                      inputWidth='w-full'
                      name={`applied_rate_active_hours${index}`}
                      value={
                        watchFormData[
                          `invoice_ndis_rate_active_hours${index}`
                        ] && getAppliedAmount()
                      }
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
     
    </>
  );
};

export default ActiveHours;
